import { customFieldDataTypes, customUDIDEntryTypes } from '../constants/Enums';

export const udidDataTypes = {
  1: 'alphabet',
  2: 'alpha_numeric',
  3: 'numeric',
  4: 'email',
  5: 'alpha_numeric_with_space',
  6: 'alpha_numeric_with_special_char',
  7: 'date_time',
};
export function isTextValidHomepageUdid(text, dataType) {
  switch (dataType) {
    case 'alphabet':
      if (!/^[A-Za-z]+$/.test(text)) {
        return 'Please enter only alphabetic characters.';
      }
      break;
    case 'alpha_numeric':
      if (!/^[A-Za-z0-9]+$/.test(text)) {
        return 'Please enter only alphanumeric characters.';
      }
      break;
    case 'numeric':
      if (!/^[0-9]+$/.test(text)) {
        return 'Please enter only numeric characters.';
      }
      break;
    case 'email':
      if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(text)) {
        return 'Please enter a valid email address.';
      }
      break;
    case 'alpha_numeric_with_space':
      if (!/^[A-Za-z0-9 ]+$/.test(text)) {
        return 'Please enter alphanumeric characters with spaces.';
      }
      break;
    case 'alpha_numeric_with_special_char':
      // Add your own regular expression for special characters
      if (!/^[ 0-9A-Za-z#$%=@@!{},`~&*()'<>?.:;_|^/+\t\r\n[\]"\\-]*$/.test(text)) {
        return 'Please enter alphanumeric characters with special characters.';
      }
      break;
    case 'date_time':
      if (!text) {
        return 'Please enter a valid date and time.';
      }
      break;
    default:
      break;
  }
  return ''; // No validation error
}

export const getCustomFieldValidation = (reportingFields, Yup, deploymentMap) => {
  const alphabetRegex = /^[A-Z]+$/i;
  const alphaNumericRegex = /^[A-Za-z0-9]*$/;
  const alphaNumericWithSpecialCharRegex = /^[ A-Za-z0-9_@.#&!?()+-]*$/; //which chars are allowed?
  const alphaNumericWithSpaceRegex = /^[ A-Za-z0-9]*$/;

  return reportingFields.reduce((object, field) => {
    const { UDIDEntryType, TextEntry, IsMandatory, UDIDText } = field;
    const { TEXT_ENTRY, PULL_DOWN, PULL_DOWN_WITH_CONDITIONAL_QUESTIONS } = customUDIDEntryTypes;
    const {
      ALPHABET,
      ALPHA_NUMERIC,
      NUMERIC,
      EMAIL,
      ALPHA_NUMERIC_WITH_SPACE,
      ALPHA_NUMERIC_WITH_SPECIAL_CHAR,
      DATE_TIME,
    } = customFieldDataTypes;
    // the input will be mandatory if it has been displayed and its mandatory from its config
    const isMandatory = deploymentMap[UDIDText] && IsMandatory;
    const errorRequiredText = 'Please fill this field';
    switch (UDIDEntryType) {
      case TEXT_ENTRY:
        switch (TextEntry) {
          case ALPHABET: {
            const errorText = 'Only alphabet letters are allowed';
            return {
              ...object,
              [UDIDText]: isMandatory
                ? Yup.string().matches(alphabetRegex, errorText).required(errorRequiredText)
                : Yup.string().matches(alphabetRegex, errorText).nullable(),
            };
          }
          case ALPHA_NUMERIC: {
            const errorText = 'Only alphabet letters and numbers are allowed';
            return {
              ...object,
              [UDIDText]: isMandatory
                ? Yup.string().matches(alphaNumericRegex, errorText).required(errorRequiredText)
                : Yup.string().matches(alphaNumericRegex, errorText).nullable(),
            };
          }
          case NUMERIC:
            return {
              ...object,
              [UDIDText]: isMandatory
                ? Yup.number().required(errorRequiredText)
                : Yup.number().nullable(),
            };
          case EMAIL:
            return {
              ...object,
              [UDIDText]: isMandatory
                ? Yup.string().email().required(errorRequiredText)
                : Yup.string().email().nullable(),
            };
          case ALPHA_NUMERIC_WITH_SPACE: {
            const errorText = 'Only alphabet letters and spaces are allowed';
            return {
              ...object,
              [UDIDText]: isMandatory
                ? Yup.string()
                    .matches(alphaNumericWithSpaceRegex, errorText)
                    .required(errorRequiredText)
                : Yup.string().matches(alphaNumericWithSpaceRegex, errorText).nullable(),
            };
          }
          case ALPHA_NUMERIC_WITH_SPECIAL_CHAR: {
            const errorText =
              'Only alphabet letters and the following symbols are allowed \n_@.#&!?()+-';
            return {
              ...object,
              [UDIDText]: isMandatory
                ? Yup.string()
                    .matches(alphaNumericWithSpecialCharRegex, errorText)
                    .required(errorRequiredText)
                : Yup.string().matches(alphaNumericWithSpecialCharRegex, errorText).nullable(),
            };
          }
          case DATE_TIME:
            return {
              ...object,
              [UDIDText]: isMandatory
                ? Yup.date().required('Please fill this date')
                : Yup.date().nullable(),
            };
          default:
            return null;
        }
      case PULL_DOWN_WITH_CONDITIONAL_QUESTIONS:
      case PULL_DOWN: {
        const errorText = 'Please select an option';
        return {
          ...object,
          [UDIDText]: isMandatory
            ? Yup.string()
                .required(errorText)
                .test(UDIDText, errorText, (val) => {
                  return val != '0';
                })
            : null,
        };
      }
      default:
        return null;
    }
  }, {});
};

export const getReportingFields = (ReportingFields, IsPerPassenger) => {
  return ReportingFields?.filter((field) => {
    const { TEXT_ENTRY, PULL_DOWN, PULL_DOWN_WITH_CONDITIONAL_QUESTIONS } = customUDIDEntryTypes;
    const { UDIDEntryType, ReportingGroupOptions, DependencyMaps } = field;
    // returning dependent inputs regardless of whether they are per passenger or not
    // because their parent decides whether or not they will be shown on the screen
    switch (UDIDEntryType) {
      case TEXT_ENTRY:
        return field.IsPerPassenger === IsPerPassenger || DependencyMaps;
      case PULL_DOWN:
      case PULL_DOWN_WITH_CONDITIONAL_QUESTIONS:
        return (
          (field.IsPerPassenger === IsPerPassenger && ReportingGroupOptions?.length > 0) ||
          (DependencyMaps && ReportingGroupOptions?.length > 0)
        );
      default:
        return false;
    }
  }).sort((a, b) => {
    // if DependencyMaps is null means that it is an independent input
    // this is sorting by dependency where an independent input will go first
    if (a.DependencyMaps === null && b.DependencyMaps !== null) {
      return -1;
    }
    if (a.DependencyMaps !== null && b.DependencyMaps === null) {
      return 1;
    }
    if (a.DependencyMaps !== null && b.DependencyMaps !== null) {
      // If both are dependents, pull down with conditional questions will go first
      const { PULL_DOWN_WITH_CONDITIONAL_QUESTIONS } = customUDIDEntryTypes;
      if (
        a.UDIDEntryType === PULL_DOWN_WITH_CONDITIONAL_QUESTIONS &&
        b.UDIDEntryType !== PULL_DOWN_WITH_CONDITIONAL_QUESTIONS
      ) {
        return -1;
      }
      if (
        a.UDIDEntryType !== PULL_DOWN_WITH_CONDITIONAL_QUESTIONS &&
        b.UDIDEntryType === PULL_DOWN_WITH_CONDITIONAL_QUESTIONS
      ) {
        return 1;
      }
      return 0;
    }
    return 0;
  });
};

export const getDeploymentMap = (reportingFields) => {
  return reportingFields?.reduce((object, field) => {
    object[field.UDIDText] = false;
    return object;
  }, {});
};
